/* eslint-disable @typescript-eslint/no-use-before-define */
import {
    getAxisPropertiesById,
    getChartSpecificCustomProps,
    getChartType,
    getColumnPropertiesById,
    getHighchartCustomisationSettings,
    getLegendColumns,
    getXAxisColumns,
    getYAxisColumns,
} from '@thoughtspot/chart-lib/lib/core/chart-model-utils';
import {
    configOptions,
    getDefaultCustomPropsForChart,
    isGeoChartType,
} from '@thoughtspot/chart-lib/lib/core/chart-type-specification-service';
import { generateQueryFromAxisConfig } from '@thoughtspot/chart-lib/lib/data/data-util';
// eslint-disable-next-line no-restricted-imports
import { AxisConfigColumn } from '@thoughtspot/chart-lib/lib/types';
import {
    CLIENT_STATE_KEYS,
    DEFAULT_ANSWER_COLUMN_PROPERTY,
    DEFAULT_AXIS_PROPERTIES,
    DEFAULT_CHART_COLUMN_PROPERTY_MAP,
    DEFAULT_CHART_SPECIFIC,
    DEFAULT_CHART_VIZ_PROPERTIES,
    DEFAULT_HEADLINE_VIZ_PROPERTY,
    DEFAULT_PERCENTAGE_FORMAT,
    DEFAULT_PIVOT_SUMMARIES_STATE,
    generateAxes,
    generateAxis,
    getUpdatedFormatConfig,
    REQUIRED_AXIS_UPGRADE_PROPERTIES,
} from '@thoughtspot/client-state-util';
import {
    DEFAULT_FORMAT_CONFIG,
    DEFAULT_PERCENTAGE_FORMAT_CONFIG,
} from '@thoughtspot/client-state-util/esm/client-state-type';
import { getId } from '@thoughtspot/visualization-column-util';
import _ from 'lodash';
import {
    AnswerColumn,
    AnswerColumnProps,
    AxisConfig,
    AxisExtremesProps,
    AxisObj,
    AxisProperties,
    AxisTypes,
    ChartProperties,
    ChartResponsiveLayout,
    ChartType,
    ChartViz,
    ChartVizProps,
    ColumnSortConfigOptions,
    FormatConfig,
    GridLineProperties,
    HandleMissingValues,
    HeadlineVizProps,
    LegendPositionOptions,
    MapViewport,
    PivotState,
    ShowPivotSummaryPrior,
    TooltipConfig,
} from '/@services/generated/graphql-types';
import {
    getAxisByColumnId,
    getColumnsInAxis,
} from '/@utils/viz/axis-properties.util';

export enum VizColumnProperties {
    updateDatalabel = 'updateDatalabel',
    updateStackAsPercent = 'updateStackAsPercent',
    updateNumberFormat = 'updateNumberFormat',
    updateColumnSortConfig = 'updateColumnSortConfig',
    updateHandleMissingValues = 'updateHandleMissingValues',
    updateExludeNullValues = 'updateExludeNullValues',
    updateAdvancedProperties = 'updateAdvancedProperties',
}

export enum HeadlineVizProperties {
    updateHeadlineNumberFormat = 'updateHeadlineNumberFormat',
    updateHeadlineDisplay = 'updateHeadlineDisplay',
}

export enum VizAxisProperties {
    updateAxisPosition = 'updateAxisPosition',
    updateAxisName = 'updateAxisName',
    updateAxisRange = 'updateAxisRange',
    updateAxisGrouping = 'updateAxisGrouping',
    updateAxisLabelFormatting = 'updateAxisLabelFormatting',
    advancedAxisProperties = 'advancedAxisProperties',
}

export enum VizProperties {
    tooltipConfig = 'tooltipConfig',
}

export enum VizChartProperties {
    allLabels = 'allLabels',
    axisExtremes = 'axisExtremes',
    dataSize = 'dataSize',
    gridLines = 'gridLines',
    isZoomed = 'isZoomed',
    mapviewport = 'mapviewport',
    showLinearRegressionLine = 'showLinearRegressionLine',
    responsiveLayoutPreference = 'responsiveLayoutPreference',
    visibleSeriesNames = 'visibleSeriesNames',
    showStackedLabels = 'showStackedLabels',
    hidePivotSummaries = 'hidePivotSummaries',
    showSubTotals = 'showSubTotals',
    showRowTotals = 'showRowTotals',
    showColumnTotals = 'showColumnTotals',
    showGrandTotals = 'showGrandTotals',
    showRowGrandTotals = 'showRowGrandTotals',
    showColumnGrandTotals = 'showColumnGrandTotals',
    isHeatmapOverlayed = 'isHeatmapOverlayed',
    pivotState = 'pivotState',
    summaryMode = 'summaryMode',
    summaryFormat = 'summaryFormat',
    useFlatLayout = 'useFlatLayout',
    dataFieldArea = 'dataFieldArea',
    showLegend = 'showLegend',
    legendPosition = 'legendPosition',
    stackedAsPercentFormat = 'stackedAsPercentFormat',
    markersEnabled = 'markersEnabled',
    isStackedAsPercent = 'isStackedAsPercent',
    showPivotSummaryPrior = 'showPivotSummaryPrior',
    customProps = 'customProps',
}

interface UpdateCustomColorSelectorArray {
    value: Array<string>;
}
export interface UpdateDataLabelOptions {
    id: VizColumnProperties.updateDatalabel;
    columnId: string;
    enabled: boolean;
}
export interface UpdateColumnCustomProperties {
    id: VizColumnProperties.updateAdvancedProperties;
    columnId: string;
    customProps: string;
}

export interface UpdateHandleMissingValues {
    id: VizColumnProperties.updateHandleMissingValues;
    columnId: string;
    value: HandleMissingValues;
}
export interface UpdateExludeNullValues {
    id: VizColumnProperties.updateExludeNullValues;
    columnId: string;
    value: boolean;
}

interface UpdateChartAxisExtremes {
    id: VizChartProperties.axisExtremes;
    value: AxisExtremesProps;
}

interface UpdateChartGridLines {
    id: VizChartProperties.gridLines;
    value: GridLineProperties;
}

interface UpdateDataSize {
    id: VizChartProperties.dataSize;
    value: number;
}

interface UpdatePivotState {
    id: VizChartProperties.pivotState;
    value: PivotState;
}

interface UpdateVisibleSeries {
    id: VizChartProperties.visibleSeriesNames;
    value: Array<string>;
}

interface UpdateResponsiveLayout {
    id: VizChartProperties.responsiveLayoutPreference;
    value: ChartResponsiveLayout;
}

interface UpdateChartFormatProperties {
    id:
        | VizChartProperties.summaryFormat
        | VizChartProperties.stackedAsPercentFormat;
    value: FormatConfig;
}

interface UpdateMapViewPort {
    id: VizChartProperties.mapviewport;
    value: MapViewport;
}

interface UpdateSummaryMode {
    id: VizChartProperties.summaryMode;
    value: string;
}

interface UpdateDataFieldArea {
    id: VizChartProperties.dataFieldArea;
    value: string;
}

interface UpdateLegendPosition {
    id: VizChartProperties.legendPosition;
    value: LegendPositionOptions;
}

export interface UpdateHeadlineVizFormat {
    id: HeadlineVizProperties.updateHeadlineNumberFormat;
    format: FormatConfig;
}

export interface UpdateAnswerColumnFormat {
    id: VizColumnProperties.updateNumberFormat;
    format: FormatConfig;
}

export interface UpdateColumnSortConfig {
    id: VizColumnProperties.updateColumnSortConfig;
    sortConfig: ColumnSortConfigOptions;
}

export interface UpdateTooltipConfig {
    id: VizProperties.tooltipConfig;
    tooltipConfig: TooltipConfig;
    chartViz: ChartViz;
}

interface UpdateCustomProps {
    id: VizChartProperties.customProps;
    value: Record<string, string | number | boolean | object>;
}

interface UpdateCustomPivotProps {
    id: VizChartProperties.showPivotSummaryPrior;
    value: ShowPivotSummaryPrior;
}

export interface UpdatePivotSummariesState {
    id:
        | VizChartProperties.showSubTotals
        | VizChartProperties.showRowTotals
        | VizChartProperties.showColumnTotals
        | VizChartProperties.showGrandTotals
        | VizChartProperties.showRowGrandTotals
        | VizChartProperties.showColumnGrandTotals;
    value: boolean;
}

export interface UpdateChartVizProperties {
    id:
        | VizChartProperties.allLabels
        | VizChartProperties.hidePivotSummaries
        | VizChartProperties.isStackedAsPercent
        | VizChartProperties.useFlatLayout
        | VizChartProperties.dataFieldArea
        | VizChartProperties.isHeatmapOverlayed
        | VizChartProperties.isZoomed
        | VizChartProperties.showLinearRegressionLine
        | VizChartProperties.markersEnabled
        | VizChartProperties.showStackedLabels
        | VizChartProperties.showLegend
        | VizChartProperties.customProps;
    value: boolean;
}

export type UpdateChartProperties =
    | UpdateChartFormatProperties
    | UpdateChartAxisExtremes
    | UpdateChartVizProperties
    | UpdateMapViewPort
    | UpdateSummaryMode
    | UpdateDataFieldArea
    | UpdateDataSize
    | UpdatePivotState
    | UpdateVisibleSeries
    | UpdateResponsiveLayout
    | UpdateChartGridLines
    | UpdateCustomProps
    | UpdateLegendPosition
    | UpdateCustomPivotProps
    | UpdatePivotSummariesState;

type UpdateAxisProperties = AxisProperties & {
    axisId: string;
    id: VizAxisProperties;
};

export type UpdateColumnProperties =
    | UpdateDataLabelOptions
    | UpdateHandleMissingValues
    | UpdateExludeNullValues;

export type ActionToValue =
    | UpdateColumnProperties
    | UpdateColumnCustomProperties
    | UpdateAxisProperties
    | UpdateChartProperties
    | UpdateAnswerColumnFormat
    | UpdateHeadlineVizFormat
    | UpdateColumnSortConfig
    | UpdateTooltipConfig;

export function getUpdatedClientStateForAnswerColumn(
    actionToValue: ActionToValue,
    oldClientState: AnswerColumnProps,
): AnswerColumnProps {
    if (!actionToValue) {
        return oldClientState;
    }

    const columnProperties = oldClientState.columnProperties || {};
    switch (actionToValue.id) {
        case VizColumnProperties.updateNumberFormat:
            return {
                ...oldClientState,
                columnProperties: {
                    ...DEFAULT_ANSWER_COLUMN_PROPERTY,
                    ...columnProperties,
                    format: getUpdatedFormatConfig(actionToValue.format),
                },
            };
        case VizColumnProperties.updateColumnSortConfig: {
            return {
                ...oldClientState,
                columnProperties: {
                    ...DEFAULT_ANSWER_COLUMN_PROPERTY,
                    ...columnProperties,
                    sortConfig: actionToValue.sortConfig,
                },
            };
        }
        default:
            return oldClientState;
    }
}

export function getUpdatedClientStateForHeadlineViz(
    actionToValue: ActionToValue,
    oldClientState: HeadlineVizProps,
): HeadlineVizProps {
    if (!actionToValue) {
        return oldClientState;
    }

    const columnProperties = oldClientState.columnProperty || {};
    switch (actionToValue.id) {
        case HeadlineVizProperties.updateHeadlineNumberFormat:
            return {
                ...oldClientState,
                columnProperty: {
                    ...DEFAULT_HEADLINE_VIZ_PROPERTY,
                    ...columnProperties,
                    summariesNumberFormatConfig: getUpdatedFormatConfig(
                        actionToValue.format,
                    ),
                },
            };
        default:
            return oldClientState;
    }
}

export function getUpdatedClientStateForCustomColorSelectorArray(
    actionToValue: UpdateCustomColorSelectorArray,
    oldClientState: ChartVizProps,
): ChartVizProps {
    return {
        ...oldClientState,
        customColorSelectorArray: actionToValue.value,
    };
}

function getUpdatedClientStateForColumn(
    actionToValue:
        | UpdateDataLabelOptions
        | UpdateHandleMissingValues
        | UpdateExludeNullValues
        | UpdateColumnCustomProperties,
    // clientState interface?
    oldClientState: ChartVizProps,
): ChartVizProps {
    if (!actionToValue) {
        return oldClientState;
    }

    const columnPropertyMap = oldClientState.columnProperties || [];
    const columnId = actionToValue.columnId;
    const columnProperties = getColumnPropertiesById(
        oldClientState,
        columnId,
    ) || {
        ...DEFAULT_CHART_VIZ_PROPERTIES,
    };
    const filteredColumnPropertyMap = _.filter(
        columnPropertyMap as any,
        colMap => colMap.columnId !== columnId,
    );

    switch (actionToValue.id) {
        case VizColumnProperties.updateDatalabel:
            return {
                ...oldClientState,
                columnProperties: [
                    ...filteredColumnPropertyMap,
                    {
                        ...DEFAULT_CHART_COLUMN_PROPERTY_MAP,
                        columnId,
                        columnProperty: {
                            ...columnProperties,
                            dataLabels: actionToValue.enabled,
                        },
                    },
                ],
            };
        case VizColumnProperties.updateHandleMissingValues:
            return {
                ...oldClientState,
                columnProperties: [
                    ...filteredColumnPropertyMap,
                    {
                        ...DEFAULT_CHART_COLUMN_PROPERTY_MAP,
                        columnId,
                        columnProperty: {
                            ...columnProperties,
                            handleMissingValues: actionToValue.value,
                        },
                    },
                ],
            };
        case VizColumnProperties.updateExludeNullValues:
            return {
                ...oldClientState,
                columnProperties: [
                    ...filteredColumnPropertyMap,
                    {
                        ...DEFAULT_CHART_COLUMN_PROPERTY_MAP,
                        columnId,
                        columnProperty: {
                            ...columnProperties,
                            excludeNullValues: actionToValue.value,
                        },
                    },
                ],
            };
        case VizColumnProperties.updateAdvancedProperties:
            return {
                ...oldClientState,
                columnProperties: [
                    ...filteredColumnPropertyMap,
                    {
                        ...DEFAULT_CHART_COLUMN_PROPERTY_MAP,
                        columnId,
                        columnProperty: {
                            ...columnProperties,
                            customProps: JSON.stringify(
                                actionToValue.customProps,
                            ) as any,
                        },
                    },
                ],
            };
        default:
            return oldClientState;
    }
}

function getUpdatedClientStateForChartProperties(
    actionToValue: UpdateChartProperties,
    oldClientState: ChartVizProps,
): ChartVizProps {
    const chartProperties = oldClientState.chartProperties || {};
    const chartSpecific = chartProperties.chartSpecific || {};
    const pivotSummariesState =
        chartSpecific.pivotSummariesState || DEFAULT_PIVOT_SUMMARIES_STATE;
    switch (actionToValue.id) {
        case VizChartProperties.allLabels:
        case VizChartProperties.axisExtremes:
        case VizChartProperties.dataSize:
        case VizChartProperties.gridLines:
        case VizChartProperties.isZoomed:
        case VizChartProperties.mapviewport:
        case VizChartProperties.showLinearRegressionLine:
        case VizChartProperties.responsiveLayoutPreference:
        case VizChartProperties.showStackedLabels:
        case VizChartProperties.visibleSeriesNames:
        case VizChartProperties.showLegend:
        case VizChartProperties.legendPosition:
            return {
                ...oldClientState,
                chartProperties: {
                    ...chartProperties,
                    [actionToValue.id]: actionToValue.value,
                },
            };
        case VizChartProperties.showPivotSummaryPrior:
        case VizChartProperties.hidePivotSummaries:
        case VizChartProperties.isHeatmapOverlayed:
        case VizChartProperties.useFlatLayout:
        case VizChartProperties.dataFieldArea:
        case VizChartProperties.markersEnabled:
        case VizChartProperties.summaryMode:
        case VizChartProperties.pivotState:
        case VizChartProperties.summaryFormat:
            return {
                ...oldClientState,
                chartProperties: {
                    ...chartProperties,
                    chartSpecific: {
                        ...DEFAULT_CHART_SPECIFIC,
                        ...chartSpecific,
                        [actionToValue.id]: actionToValue.value,
                    },
                },
            };
        case VizChartProperties.showRowTotals:
        case VizChartProperties.showColumnTotals:
        case VizChartProperties.showRowGrandTotals:
        case VizChartProperties.showColumnGrandTotals:
            return {
                ...oldClientState,
                chartProperties: {
                    ...chartProperties,
                    chartSpecific: {
                        ...DEFAULT_CHART_SPECIFIC,
                        ...chartSpecific,
                        pivotSummariesState: {
                            ...pivotSummariesState,
                            [actionToValue.id]: actionToValue.value,
                        },
                    },
                },
            };
        case VizChartProperties.showSubTotals:
            return {
                ...oldClientState,
                chartProperties: {
                    ...chartProperties,
                    chartSpecific: {
                        ...DEFAULT_CHART_SPECIFIC,
                        ...chartSpecific,
                        pivotSummariesState: {
                            ...pivotSummariesState,
                            showRowTotals: actionToValue.value,
                            showColumnTotals: actionToValue.value,
                        },
                    },
                },
            };
        case VizChartProperties.showGrandTotals:
            return {
                ...oldClientState,
                chartProperties: {
                    ...chartProperties,
                    chartSpecific: {
                        ...DEFAULT_CHART_SPECIFIC,
                        ...chartSpecific,
                        pivotSummariesState: {
                            ...pivotSummariesState,
                            showRowGrandTotals: actionToValue.value,
                            showColumnGrandTotals: actionToValue.value,
                        },
                    },
                },
            };
        case VizChartProperties.customProps:
            return {
                ...oldClientState,
                chartProperties: {
                    ...chartProperties,
                    chartSpecific: {
                        ...DEFAULT_CHART_SPECIFIC,
                        ...chartSpecific,
                        [VizChartProperties.customProps]: JSON.stringify(
                            actionToValue.value,
                        ) as any,
                    },
                },
            };
        case VizChartProperties.isStackedAsPercent:
            return {
                ...oldClientState,
                chartProperties: {
                    ...chartProperties,
                    chartSpecific: {
                        ...DEFAULT_CHART_SPECIFIC,
                        ...chartSpecific,
                        isStackedAsPercent: actionToValue.value,
                        stackedAsPercentFormat: actionToValue.value
                            ? chartSpecific?.stackedAsPercentFormat ||
                              DEFAULT_PERCENTAGE_FORMAT
                            : chartSpecific?.stackedAsPercentFormat || null,
                    },
                },
            };
        case VizChartProperties.stackedAsPercentFormat:
            return {
                ...oldClientState,
                chartProperties: {
                    ...chartProperties,
                    chartSpecific: {
                        ...DEFAULT_CHART_SPECIFIC,
                        ...chartSpecific,
                        stackedAsPercentFormat: {
                            ...DEFAULT_FORMAT_CONFIG,
                            ...actionToValue.value,
                            percentageFormatConfig: {
                                ...DEFAULT_PERCENTAGE_FORMAT_CONFIG,
                                ...actionToValue.value.percentageFormatConfig,
                            },
                        },
                    },
                },
            };
        default:
            return oldClientState;
    }
}

const updateAxisConfigOnGrouping = (
    axisId: string,
    columnId: string,
    vizProp: ChartVizProps,
    axisConfig: AxisConfig,
): AxisConfig => {
    // When grouping or ungrouping is done AxisConfig should be updated to
    // maintain group order as groups are always placed conscecutively.
    // Find order of yAxisColumns in AxisConfig
    const yAxisColumns = axisConfig.y;
    // Remove the column from axisConfig which need to be replaced so that
    // we can place it at its correct index.
    const updatedYAxisColumns = yAxisColumns.filter(
        col => columnId !== getId(col),
    );
    // Denotes the column which we are grouping in current axis.
    const columnToBeReplaced = yAxisColumns.find(
        col => columnId === getId(col),
    );
    // Find the index of last column of grouped axis in axis config so that we can insert
    // columnToBeReplaced at this position
    const lastColInAxisToBeGrouped: string = getColumnsInAxis(
        vizProp.axisProperties,
        axisId,
    ).slice(-1)[0];
    // Find index of last column of current grouped axis in which we are grouping
    // or ungrouping column.
    const indexOfLastCol = _.findIndex(
        updatedYAxisColumns,
        col => getId(col) === lastColInAxisToBeGrouped,
    );
    // insert columnToBeReplaced at this position of AxisConfig
    updatedYAxisColumns.splice(indexOfLastCol + 1, 0, columnToBeReplaced);
    return {
        ...axisConfig,
        y: [...updatedYAxisColumns],
    };
};

const removeColumnFromAxis = (
    columnId: string,
    oldClientState: ChartVizProps,
): ChartVizProps => {
    const axisProperties = oldClientState.axisProperties;
    const axisWithColumn = getAxisByColumnId(axisProperties, columnId);
    const columnsInAxis = getColumnsInAxis(axisProperties, axisWithColumn.id);
    const filteredAxes = _.filter(
        axisProperties,
        axis => axis.id !== axisWithColumn.id,
    );
    if (columnsInAxis.length === 1) {
        // remove axis from axis properties
        return {
            ...oldClientState,
            axisProperties: [...filteredAxes],
        };
    }
    // remove column from axis
    const updatedColumnsInAxis = _.filter(
        columnsInAxis,
        colId => colId !== columnId,
    );
    const updatedAxisProperties: AxisProperties = {
        ...axisWithColumn.properties,
        linkedColumns: [...updatedColumnsInAxis],
    };
    return {
        ...oldClientState,
        axisProperties: [
            ...filteredAxes,
            { id: axisWithColumn.id, properties: updatedAxisProperties },
        ],
    };
};

const addColumnToAxis = (
    columnId: string,
    axisId: string,
    oldClientState: ChartVizProps,
) => {
    const axisProperties = [...oldClientState.axisProperties];
    const axisWithCurrentAxisId = axisProperties.find(
        axisObj => axisObj.id === axisId,
    );
    const columnsInAxis = getColumnsInAxis(axisProperties, axisId);
    // TODO:Need to figure out what happens to YAxisRange or any other property that gets
    // affected
    const updatedAxisProperties: AxisProperties = {
        ...axisWithCurrentAxisId.properties,
        linkedColumns: [...columnsInAxis, columnId],
    };
    const filteredAxes = _.filter(
        axisProperties,
        axisObj => axisObj.id !== axisId,
    );

    return {
        ...oldClientState,
        axisProperties: [
            ...filteredAxes,
            {
                id: axisWithCurrentAxisId.id,
                properties: updatedAxisProperties,
            },
        ],
    };
};

const addColumnToNewAxis = (
    columnId: string,
    additionalProps: Partial<AxisProperties>,
    vizProp: ChartVizProps,
) => {
    const axisProperties = vizProp.axisProperties;
    const axisProps = {
        ...REQUIRED_AXIS_UPGRADE_PROPERTIES,
        ...additionalProps,
    };

    if (!axisProps || _.isNil(axisProps.isOpposite)) {
        axisProps.isOpposite = axisProperties.some(axisObj => {
            return (
                axisObj.properties.axisType === AxisTypes.Y &&
                !axisObj.properties.isOpposite
            );
        });
    }

    const newAxis = generateAxis([columnId], AxisTypes.Y, axisProps);
    return {
        ...vizProp,
        axisProperties: [...axisProperties, newAxis],
    };
};

export const getUpdatedConfigAfterAxisGrouping = (
    columnId: string,
    axisId: string,
    oldClientState: ChartVizProps,
    axisConfig: AxisConfig,
) => {
    const axisProperties = oldClientState.axisProperties;
    const columnsInAxis = getColumnsInAxis(axisProperties, axisId);
    const columnPresentInCurrentAxis =
        _.indexOf(columnsInAxis, columnId) !== -1;

    let newClientState = { ...oldClientState };
    let newAxisConfig = { ...axisConfig };

    // If clicked column is already in current axis, it's an Ungroup case, else Group.
    // In any case, remove the column from the axis where it's present. Then:
    // 1. For Grouping: Add it to current axis
    // 2. For Ungrouping: Add it to a new axis
    if (columnPresentInCurrentAxis) {
        newClientState = removeColumnFromAxis(columnId, oldClientState);
        newClientState = addColumnToNewAxis(columnId, {}, newClientState);
        newAxisConfig = updateAxisConfigOnGrouping(
            axisId,
            columnId,
            oldClientState,
            axisConfig,
        );
        // collectEvent(new UserAction(userActionTypeEnum.GROUP_AXIS), {
        //     columnId,
        //     axisId: this.axisActionConfig.axisId,
        //     chartType: this.chartModel.getChartType()
        // });
    } else {
        newClientState = removeColumnFromAxis(columnId, oldClientState);
        newClientState = addColumnToAxis(columnId, axisId, newClientState);
        newAxisConfig = updateAxisConfigOnGrouping(
            axisId,
            columnId,
            oldClientState,
            axisConfig,
        );
        // collectEvent(new UserAction(userActionTypeEnum.UNGROUP_AXIS), {
        //     columnId,
        //     axisId: this.axisActionConfig.axisId,
        //     chartType: this.chartModel.getChartType()
        // });
    }
    return {
        clientState: newClientState,
        axisConfig: newAxisConfig,
    };
};

function getUpdatedClientStateForAxis(
    actionToValue: UpdateAxisProperties,
    oldClientState: ChartVizProps,
): ChartVizProps {
    // deep copy the axis properties
    const axisProperties = [...(oldClientState.axisProperties || [])];
    const axisId = actionToValue.axisId;

    // get the axis property by id
    const axisProperty = {
        ...(getAxisPropertiesById(oldClientState, axisId) ||
            DEFAULT_AXIS_PROPERTIES),
    };
    let axisIndex = axisProperties.findIndex(axis => axis.id === axisId);
    // if the axis is not found, add it to the end of the array
    if (axisIndex === -1) {
        axisProperties.push({
            id: axisId,
            properties: axisProperty,
        });
        axisIndex = axisProperties.length - 1;
    }
    switch (actionToValue.id) {
        case VizAxisProperties.updateAxisName:
            axisProperty.name = actionToValue.name;
            axisProperty.dynamicTitle = actionToValue.dynamicTitle ?? null;
            break;
        case VizAxisProperties.updateAxisPosition:
            axisProperty.isOpposite = actionToValue.isOpposite;
            break;
        case VizAxisProperties.updateAxisRange:
            axisProperty.yAxisRange = actionToValue.yAxisRange;
            break;
        case VizAxisProperties.updateAxisLabelFormatting:
            axisProperty.format = getUpdatedFormatConfig(actionToValue.format);
            break;
        case VizAxisProperties.advancedAxisProperties:
            axisProperty.customProps = JSON.stringify(
                actionToValue.customProps,
            ) as any;
            break;
        default:
            return oldClientState;
    }
    // update the axis property by index to not change the order of the array
    axisProperties[axisIndex] = {
        ...axisProperties[axisIndex],
        properties: axisProperty,
    };
    return {
        ...oldClientState,
        axisProperties,
    };
}

const updateAdvancedPropsTooltipFlag = (
    chartViz: ChartViz,
    vizProp: ChartVizProps,
): ChartVizProps => {
    let advancedProperties = getHighchartCustomisationSettings(chartViz);
    if (!advancedProperties) {
        advancedProperties = {};
    }
    if (!advancedProperties.tooltip) {
        advancedProperties.tooltip = {};
    }
    const tooltipConfig = vizProp.tooltipConfig;
    const isTooltipEnabled = tooltipConfig?.columnIds?.length !== 0;

    // toggle on advanced props tooltip flag if any column is toggled on in custom tooltip modal
    // toggle off advanced props tooltip flag if all the columns are toggled off in custom tooltip modal
    advancedProperties = {
        ...advancedProperties,
        tooltip: {
            ...advancedProperties.tooltip,
            enabled: isTooltipEnabled,
        },
    };
    const defaultCustomProps =
        getDefaultCustomPropsForChart(getChartType(chartViz)) || {};
    const currentCustomProps = getChartSpecificCustomProps(chartViz) || {};
    const updatedCustomProps = {
        ...defaultCustomProps,
        ...currentCustomProps,
        advancedProperties,
    };
    const updatedVizProps = getUpdatedClientState(
        CLIENT_STATE_KEYS.chartProperties,
        {
            id: VizChartProperties.customProps,
            value: updatedCustomProps,
        },
        vizProp,
    );

    return updatedVizProps;
};

export function getUpdatedClientStateForTooltipConfig(
    actionToValue: UpdateTooltipConfig,
    oldClientState: ChartVizProps,
): ChartVizProps {
    const { tooltipConfig, chartViz } = actionToValue;
    const updatedClientState = {
        ...oldClientState,
        tooltipConfig,
    };
    return updateAdvancedPropsTooltipFlag(chartViz, updatedClientState);
}

export function getUpdatedClientState(
    updateProperty: string,
    actionToValue: ActionToValue,
    oldClientState: ChartVizProps,
) {
    switch (updateProperty) {
        case CLIENT_STATE_KEYS.chartProperties:
            return getUpdatedClientStateForChartProperties(
                actionToValue as UpdateChartProperties,
                oldClientState,
            );
        case CLIENT_STATE_KEYS.axisProperties:
            return getUpdatedClientStateForAxis(
                actionToValue as UpdateAxisProperties,
                oldClientState,
            );
        case CLIENT_STATE_KEYS.columnProperties:
            return getUpdatedClientStateForColumn(
                actionToValue as UpdateDataLabelOptions,
                oldClientState,
            );
        case CLIENT_STATE_KEYS.customColorSelectorArray:
            return getUpdatedClientStateForCustomColorSelectorArray(
                actionToValue as UpdateCustomColorSelectorArray,
                oldClientState,
            );
        case CLIENT_STATE_KEYS.tooltipConfig:
            return getUpdatedClientStateForTooltipConfig(
                actionToValue as UpdateTooltipConfig,
                oldClientState,
            );

        default:
            return oldClientState;
    }
}

// this will not be required once prism exposes columnId to column map
export function findColumnFromAxisConfig(
    axisConfig: AxisConfig,
    columnId: string,
) {
    const keys = Object.keys(axisConfig);
    let arr: AnswerColumn[] = [];
    keys.forEach((key: keyof AxisConfig) => {
        arr = arr.concat(axisConfig[key] || ([] as any));
    });

    return arr.find((column: AnswerColumn) => column.id === columnId);
}

export function arraysContainSameValues(firstArray: any, secondArray: any) {
    return _.xor(firstArray, secondArray).length === 0;
}

export const reuseChartProperties = (
    chartModel: ChartViz,
    vizProp: ChartVizProps,
    newAxisConfig: AxisConfig,
): ChartProperties => {
    const modifiedChartProperties = _.cloneDeep(vizProp.chartProperties);
    const newLegendColIds = newAxisConfig.color?.map(
        (col: AnswerColumn) => col.id,
    );
    if (
        !arraysContainSameValues(
            getLegendColumns(chartModel).map((col: AnswerColumn) => col.id),
            newLegendColIds,
        )
    ) {
        modifiedChartProperties.visibleSeriesNames = [];
    }
    const yAxesColumns = getYAxisColumns(chartModel);
    const hasMultiYAxisLegend = yAxesColumns ? yAxesColumns.length > 1 : false;
    if (
        hasMultiYAxisLegend &&
        !arraysContainSameValues(newAxisConfig.y, yAxesColumns)
    ) {
        modifiedChartProperties.visibleSeriesNames = [];
    }
    const xAxesColumns = getXAxisColumns(chartModel);
    if (!arraysContainSameValues(newAxisConfig.x, xAxesColumns)) {
        if (isGeoChartType(chartModel.config.chartType)) {
            modifiedChartProperties.mapviewport = null;
        } else {
            modifiedChartProperties.responsiveLayoutPreference = null;
            modifiedChartProperties.isZoomed = null;
            modifiedChartProperties.axisExtremes = null;
        }
    }

    return modifiedChartProperties;
};
export function reuseYAxisGrouping(
    yAxesGroupedInOldConfig: AxisObj[],
    newYaxisColIds: string[],
    newChartCanContainOnlyOneYAxis: boolean,
    oldChartCanContainOnlyOneYAxis: boolean,
): AxisObj[] {
    const linkedColumnIdsInGroupedAxis: string[] = [];
    const groupedYAxisProperties: AxisObj[] = [];
    if (
        !_.isEmpty(yAxesGroupedInOldConfig) &&
        !(newChartCanContainOnlyOneYAxis || oldChartCanContainOnlyOneYAxis)
    ) {
        _.forEach(yAxesGroupedInOldConfig, (groupedAxis: AxisObj) => {
            const linkedColumns: string[] =
                groupedAxis.properties.linkedColumns;

            const activeGroupedColumns = _.intersection(
                linkedColumns,
                newYaxisColIds,
            );

            if (activeGroupedColumns.length > 1) {
                groupedYAxisProperties.push(
                    generateAxes(activeGroupedColumns, AxisTypes.Y, {
                        isLinkColumns: true,
                        additionalProperties: REQUIRED_AXIS_UPGRADE_PROPERTIES,
                    })[0] as AxisObj,
                );
                linkedColumnIdsInGroupedAxis.push(...activeGroupedColumns);
            }
        });
    }

    // Filtering out grouped columnIds and building ungrouped columns
    const filteredNewYAxisColIds = newYaxisColIds.filter(
        id => !linkedColumnIdsInGroupedAxis.includes(id),
    );

    // Since config is changed we need to regenerate axes
    const yAxisProperties = generateAxes(filteredNewYAxisColIds, AxisTypes.Y, {
        isLinkColumns: newChartCanContainOnlyOneYAxis,
        additionalProperties: REQUIRED_AXIS_UPGRADE_PROPERTIES,
    });

    return [...groupedYAxisProperties, ...yAxisProperties];
}

function getColumnsIdsInAxis(axisConfig: AxisConfig) {
    const visualizedColumns = generateQueryFromAxisConfig(
        axisConfig,
    ).getColumnsInDataOrder();
    return visualizedColumns.map((col: AxisConfigColumn) => col.id);
}

function getNewVisualizedColumns(
    newAxisConfig: AxisConfig,
    oldAxisConfig: AxisConfig,
): string[] {
    const newColumnIds = getColumnsIdsInAxis(newAxisConfig);
    const oldColumnIds = getColumnsIdsInAxis(oldAxisConfig);
    return _.difference(newColumnIds, oldColumnIds);
}

function getNewNonVisualizedColumns(
    newAxisConfig: AxisConfig,
    oldAxisConfig: AxisConfig,
): string[] {
    const newColumnIds = getColumnsIdsInAxis(newAxisConfig);
    const oldColumnIds = getColumnsIdsInAxis(oldAxisConfig);
    return _.difference(oldColumnIds, newColumnIds);
}

export function reuseTooltipConfig(
    tooltipConfig: TooltipConfig,
    newAxisConfig: AxisConfig,
    oldAxisConfig: AxisConfig,
): TooltipConfig {
    if (!tooltipConfig) {
        return null;
    }
    const tooltipColumnIds = [...tooltipConfig.columnIds];
    const newlyAddedColumnsIds = getNewVisualizedColumns(
        newAxisConfig,
        oldAxisConfig,
    );
    const newlyRemovedColumnIds = getNewNonVisualizedColumns(
        newAxisConfig,
        oldAxisConfig,
    );

    const updatedTooltipColumnIds = [
        ..._.difference(tooltipColumnIds, newlyRemovedColumnIds),
        ...newlyAddedColumnsIds,
    ];
    const uniqTooltipColumnIds = _.uniq(updatedTooltipColumnIds);

    return {
        columnIds: uniqTooltipColumnIds,
    };
}

export function reuseAxesIfPossible(
    // still need chartModel to be passed into configOptions.
    // can possibly try to refactor that to remove dependency
    chartModel: ChartViz,
    newAxisConfig: AxisConfig, // ChartAxisConfig,
    oldChartType: ChartType, // chartTypesEnum,
    newChartType: ChartType, // chartTypesEnum
): any {
    const newYaxisColIds = newAxisConfig.y.map((col: AnswerColumn) => col.id);
    const newXAxisColIds = newAxisConfig.x.map((col: AnswerColumn) => col.id);

    const areYAxisColumnsSameInNewConfig = arraysContainSameValues(
        getYAxisColumns(chartModel).map((col: AnswerColumn) => col.id),
        newYaxisColIds,
    );
    const areXAxisColumnsSameInNewConfig = arraysContainSameValues(
        getXAxisColumns(chartModel).map((col: AnswerColumn) => col.id),
        newXAxisColIds,
    );

    const oldChartCanContainOnlyOneYAxis = configOptions(
        oldChartType,
        chartModel,
    ).allowedConfigurations.shouldOnlyContainOneYAxis;

    // check if any axes were grouped in old config
    const yAxesGroupedInOldConfig: any = [];

    // Push the y axis object itself in case it is grouped
    _.forEach(chartModel.vizProp.axisProperties, axis => {
        if (
            axis.properties.axisType === AxisTypes.Y &&
            axis.properties.linkedColumns.length > 1
        ) {
            yAxesGroupedInOldConfig.push(axis);
        }
    });

    const newChartCanContainOnlyOneYAxis = configOptions(
        newChartType,
        chartModel,
    ).allowedConfigurations.shouldOnlyContainOneYAxis;

    // Now if axis config is exactly same then no point of generating new axis
    if (
        oldChartCanContainOnlyOneYAxis === newChartCanContainOnlyOneYAxis &&
        areYAxisColumnsSameInNewConfig &&
        areXAxisColumnsSameInNewConfig
    ) {
        // if these conditions are satisfied, it means that we don't have to generate new axes and
        // we can simply reuse older axes which will retain properties.
        return false;
    }

    const yAxisProperties = reuseYAxisGrouping(
        yAxesGroupedInOldConfig,
        newYaxisColIds,
        newChartCanContainOnlyOneYAxis,
        oldChartCanContainOnlyOneYAxis,
    );

    const xAxisProperties = generateAxes(newXAxisColIds, AxisTypes.X, {
        additionalProperties: REQUIRED_AXIS_UPGRADE_PROPERTIES,
    });

    return [...yAxisProperties, ...xAxisProperties];
}

const testHooks = {
    updateAxisConfigOnGrouping,
    updateAdvancedPropsTooltipFlag,
};

export const updateInheritedProperties = (newVizProp: any) => {
    // Find objects with isLinked: true
    const findAndUpdateLinkedObjects = (obj: any, path = '') => {
        if (!obj || obj === null || typeof obj !== 'object') return;

        // Check if current object has isLinked: true
        if (obj.isLinked === true && obj?.linkedTo != null) {
            const { type, id, key } = obj?.linkedTo;
            if (type === 'AXIS') {
                const axisProperties = newVizProp.axisProperties[id];
                const props = axisProperties[key];
                if (props || props != null) {
                    _.set(newVizProp, path, {
                        ...props,
                        isLinked: true,
                        linkedTo: obj?.linkedTo,
                    });
                }
            } else if (type === 'DATA_LABEL_PROPS') {
                const dataLabelProperties = newVizProp.dataLabelProperties[id];
                const props = dataLabelProperties[key];
                if (props || props != null) {
                    _.set(newVizProp, path, {
                        ...props,
                        isLinked: true,
                        linkedTo: obj?.linkedTo,
                    });
                }
            }
        }

        // Recursively check all properties
        Object.entries(obj).forEach(([key, value]) => {
            if (value && typeof value === 'object') {
                findAndUpdateLinkedObjects(
                    value,
                    path ? `${path}.${key}` : key,
                );
            }
        });
    };

    findAndUpdateLinkedObjects(newVizProp);
};

export const unlinkAllInheritance = (
    clientState: ChartVizProps,
): ChartVizProps => {
    const result = { ...clientState };
    if (result.axisProperties) {
        result.axisProperties = result.axisProperties.map(axis => {
            if (axis?.properties?.customProps) {
                const customProps = axis.properties.customProps.replaceAll(
                    '"isLinked":true',
                    '"isLinked":false',
                );
                return {
                    ...axis,
                    properties: {
                        ...axis.properties,
                        customProps,
                    },
                };
            }
            return axis;
        });

        result.columnProperties = result.columnProperties.map(column => {
            if (column.columnProperty.customProps) {
                const customProps = column.columnProperty.customProps.replaceAll(
                    '"isLinked":true',
                    '"isLinked":false',
                );
                return {
                    ...column,
                    columnProperty: {
                        ...column.columnProperty,
                        customProps,
                    },
                };
            }
            return column;
        });
    }
    return result;
};

export { testHooks };
